// Étape 10 — Page admin « Modèles » : versions + aperçus PDF remplis avec un client fictif
import { useState, useEffect } from "react";
import axios from "axios";
import { C, Btn, Input, Alert, Spinner } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hdr = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("auprea_token")}` } });

const Toggle = ({ label, value, onChange, testid }) => (
  <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12, cursor: "pointer" }}>
    <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} data-testid={testid} /> {label}
  </label>
);

const Editeur = ({ code, onClose, onSaved }) => {
  const [t, setT] = useState(null);
  const [version, setVersion] = useState("");
  const [html, setHtml] = useState("");
  const [err, setErr] = useState("");
  useEffect(() => { axios.get(`${API}/admin/modeles/${code}`, hdr()).then(r => { setT(r.data); setHtml(r.data.html); }); }, [code]);
  if (!t) return <Spinner />;
  const save = async () => {
    setErr("");
    try { await axios.post(`${API}/admin/modeles/${code}`, { version, html }, hdr()); onSaved(); onClose(); }
    catch (e) { setErr(e?.response?.data?.detail || "Erreur"); }
  };
  return (
    <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 12, padding: 16, marginTop: 12 }} data-testid="modele-editeur">
      <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 6 }}>Nouvelle version — {t.nom}</div>
      <div style={{ fontSize: 11, color: C.mid, marginBottom: 10 }}>
        Une nouvelle version ne modifie jamais un document déjà envoyé ou signé. Variables {"{{ }}"} et blocs [SI …] [FIN SI] acceptés.
        Versions existantes : {t.versions_list.map(v => `${v.version} (${(v.created_at || "").slice(0, 10)})`).join(" · ")}
      </div>
      {err && <Alert type="error"><span>⚠</span><span>{err}</span></Alert>}
      <Input value={version} onChange={(e) => setVersion(e.target.value)} placeholder="N° de la nouvelle version (ex. 2 ou 2026-11)" data-testid="modele-version" style={{ marginBottom: 8 }} />
      <textarea value={html} onChange={(e) => setHtml(e.target.value)} data-testid="modele-html"
        style={{ width: "100%", minHeight: 320, fontFamily: "monospace", fontSize: 11, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10 }} />
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <Btn onClick={onClose}>Annuler</Btn>
        <Btn variant="gold" onClick={save} disabled={!version.trim()} data-testid="modele-save">Enregistrer la version</Btn>
      </div>
    </div>
  );
};

export default function ModelesAdmin() {
  const [items, setItems] = useState(null);
  const [code, setCode] = useState("proposition");
  const [opts, setOpts] = useState({ public: "particulier", retractation: true, deux_fois: false });
  const [pdf, setPdf] = useState(null);
  const [loading, setLoading] = useState(false);
  const [edit, setEdit] = useState(false);

  const loadList = () => axios.get(`${API}/admin/modeles`, hdr()).then(r => setItems(r.data));
  useEffect(() => { loadList(); }, []);
  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams({ public: opts.public, retractation: opts.retractation, deux_fois: opts.deux_fois });
    axios.get(`${API}/admin/modeles/${code}/apercu?${q}`, { ...hdr(), responseType: "blob" })
      .then(r => setPdf(old => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(r.data); }))
      .finally(() => setLoading(false));
  }, [code, opts]);

  if (!items) return <Spinner />;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "280px 1fr", gap: 20 }} data-testid="modeles-page">
      <div>
        {items.map(m => (
          <button key={m.code} onClick={() => { setCode(m.code); setEdit(false); }} data-testid={`modele-${m.code}`}
            style={{ display: "block", width: "100%", textAlign: "left", background: code === m.code ? C.navy : "#fff", color: code === m.code ? "#fff" : C.navy,
              border: `.5px solid ${C.border}`, borderRadius: 10, padding: "12px 14px", marginBottom: 8, cursor: "pointer", fontFamily: "inherit" }}>
            <div style={{ fontSize: 13, fontWeight: 700 }}>{m.nom}</div>
            <div style={{ fontSize: 11, opacity: .75, marginTop: 3 }}>Version {m.version} · {(m.date || "").slice(0, 10)} · {m.versions} version(s)</div>
          </button>
        ))}
      </div>
      <div>
        <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap", background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 10, padding: "10px 14px", marginBottom: 12 }}>
          <div style={{ display: "flex", gap: 4 }}>
            {[["particulier", "Particulier"], ["pro", "Professionnel"]].map(([k, l]) => (
              <Btn key={k} small variant={opts.public === k ? "gold" : "ghost"} onClick={() => setOpts({ ...opts, public: k })} data-testid={`apercu-public-${k}`}>{l}</Btn>
            ))}
          </div>
          <Toggle label="Avec rétractation (présentiel)" value={opts.retractation} onChange={(v) => setOpts({ ...opts, retractation: v })} testid="apercu-retractation" />
          <Toggle label="Paiement en 2 fois" value={opts.deux_fois} onChange={(v) => setOpts({ ...opts, deux_fois: v })} testid="apercu-deux-fois" />
          <div style={{ flex: 1 }} />
          {pdf && <a href={pdf} download={`apercu-${code}.pdf`} data-testid="apercu-download" style={{ fontSize: 12, color: C.teal, fontWeight: 600 }}>Télécharger le PDF</a>}
          <Btn small onClick={() => setEdit(!edit)} data-testid="modele-new-version">✎ Nouvelle version</Btn>
        </div>
        <div style={{ fontSize: 11, color: C.mid, marginBottom: 8 }}>Aperçu rempli avec un client fictif. Les champs non renseignés dans les Réglages apparaissent surlignés en orange.</div>
        {edit && <Editeur code={code} onClose={() => setEdit(false)} onSaved={() => { loadList(); setOpts({ ...opts }); }} />}
        <div style={{ position: "relative", marginTop: 12 }}>
          {loading && <div style={{ position: "absolute", top: 20, left: 20, fontSize: 12, color: C.mid }}>Génération du PDF…</div>}
          {pdf && <iframe title="Aperçu" src={pdf} data-testid="apercu-iframe" style={{ width: "100%", height: "80vh", border: `.5px solid ${C.border}`, borderRadius: 10, background: "#fff" }} />}
        </div>
      </div>
    </div>
  );
}
